/**
 * InvoicesSubTab — invoice ↔ property ↔ room linkage. Sub-tab inside the new Services tab. Lifted from the prior flat ServicesTab on 2026-05-29 with no logic changes, only its import paths and export name updated.
 *
 * Source of truth: hosp_service_history.
 *
 * view mode — read-only list of invoices + linked rooms, no expand, no Link
 * edit mode — click an invoice to expand room selection, tick rooms, Link.
 */

import { useEffect, useState } from 'react';
import { formatDateMedium } from '../../../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import { StatusBadge } from '../../../../../../components/DataTable';
import { useConfig } from '../../../../../../contexts/ConfigContext';
import { TIER_FREQUENCIES, POS_GOLD } from '../../constants';
import { suggestFromServiceLog, type ServiceLogItem } from '../rooms-matrix/suggestionEngines';
import type { EnrichedProperty, PropertyMode } from '../types';

interface EnrichedInvoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  total: number;
  status: string;
  service_log_id: string | null;
  line_items: unknown;
  isLinked: boolean;
  linkedRoomIds: string[];
  items: { name: string; quantity: number }[];
}

interface Room {
  id: string;
  room_number: string;
  room_name: string | null;
  bed_count: number | null;
  last_service_date: string | null;
}

interface ServicesTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
  mode: PropertyMode;
  onLinked?: () => void;
}

export default function InvoicesSubTab({ supabase, property, mode, onLinked }: ServicesTabProps) {
  const { formatCurrency } = useConfig();
  const [invoices, setInvoices] = useState<EnrichedInvoice[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedRooms, setSelectedRooms] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [successCount, setSuccessCount] = useState(0);
  const [seedingFor, setSeedingFor] = useState<string | null>(null);

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id]);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      // Always refetch customer_id from the DB — prop could be stale.
      const { data: fresh } = await supabase
        .from('hosp_properties')
        .select('customer_id')
        .eq('id', property.id)
        .single();
      const customerId = fresh?.customer_id || property.customer_id;
      if (!customerId) {
        setInvoices([]);
        setRooms([]);
        setLoading(false);
        return;
      }

      // Only select columns that actually exist on invoices. Line items
      // live in the `invoice_items` table — fetch separately if needed.
      const { data: custInvoices, error: invErr } = await supabase
        .from('invoices')
        .select('id, invoice_number, customer_name, invoice_date, total, status, service_log_id')
        .eq('customer_id', customerId)
        .in('status', ['paid', 'sent', 'overdue', 'partial'])
        .order('invoice_date', { ascending: false })
        .limit(50);
      if (invErr) throw invErr;

      const { data: roomData } = await supabase
        .from('hosp_rooms')
        .select('id, room_number, room_name, bed_count, last_service_date')
        .eq('property_id', property.id)
        .eq('status', 'active')
        .order('room_number');
      setRooms((roomData as Room[] | null) || []);

      const invoiceIds = (custInvoices || []).map((i: Record<string, unknown>) => i.id as string);
      let historyLinks: { invoice_id: string; room_id: string | null }[] = [];
      if (invoiceIds.length > 0) {
        const { data: history } = await supabase
          .from('hosp_service_history')
          .select('invoice_id, room_id')
          .eq('property_id', property.id)
          .in('invoice_id', invoiceIds);
        historyLinks = (history as { invoice_id: string; room_id: string | null }[] | null) || [];
      }

      const linkedInfo: Record<string, { roomIds: string[] }> = {};
      historyLinks.forEach(h => {
        if (!linkedInfo[h.invoice_id]) linkedInfo[h.invoice_id] = { roomIds: [] };
        if (h.room_id) linkedInfo[h.invoice_id]!.roomIds.push(h.room_id);
      });

      const enriched: EnrichedInvoice[] = (custInvoices || []).map((inv: Record<string, unknown>) => ({
        ...(inv as unknown as EnrichedInvoice),
        isLinked: !!linkedInfo[inv.id as string],
        linkedRoomIds: linkedInfo[inv.id as string]?.roomIds || [],
        items: [],
      })) as EnrichedInvoice[];

      setInvoices(enriched);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    return formatDateMedium(dateStr);
  }

  function toggleRoom(invoiceId: string, roomId: string) {
    setSelectedRooms(prev => {
      const current = new Set(prev[invoiceId] || []);
      if (current.has(roomId)) current.delete(roomId);
      else current.add(roomId);
      return { ...prev, [invoiceId]: current };
    });
  }

  function selectAllRooms(invoiceId: string) {
    setSelectedRooms(prev => ({ ...prev, [invoiceId]: new Set(rooms.map(r => r.id)) }));
  }

  function selectNoneRooms(invoiceId: string) {
    setSelectedRooms(prev => ({ ...prev, [invoiceId]: new Set() }));
  }

  /**
   * One-click "seed rooms from this invoice". Three sources, tried in
   * order:
   *   1. The service log linked to this invoice (modern flow).
   *   2. The property's most recent non-draft service log (modern
   *      flow, when the invoice itself was issued before logs were
   *      first-class).
   *   3. This invoice's own invoice_items (legacy properties that
   *      were captured before service_logs existed at all, e.g.
   *      Villa Rosa). Same shape as service_log_items, fed through
   *      the same engine which now canonicalises singular / plural
   *      item-name variants.
   */
  async function handleSeedRoomsFromLog(invoice: EnrichedInvoice) {
    setSeedingFor(invoice.id);
    setError(null);
    try {
      let items: ServiceLogItem[] = [];

      // Step 1 + 2: look for a service log.
      let logId = invoice.service_log_id;
      if (!logId) {
        const { data: latest } = await supabase
          .from('service_logs')
          .select('id')
          .eq('property_id', property.id)
          .neq('status', 'draft')
          .order('service_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        logId = (latest as { id?: string } | null)?.id ?? null;
      }
      if (logId) {
        const { data: itemRows, error: itemErr } = await supabase
          .from('service_log_items')
          .select('section_label, section_quantity, item_name, quantity')
          .eq('service_log_id', logId);
        if (itemErr) throw itemErr;
        items = (itemRows ?? []) as ServiceLogItem[];
      }

      // Step 3: fall back to the invoice's own line items (legacy).
      if (items.length === 0) {
        const { data: invItems, error: invItemErr } = await supabase
          .from('invoice_items')
          .select('section_label, section_quantity, item_name, quantity')
          .eq('invoice_id', invoice.id);
        if (invItemErr) throw invItemErr;
        items = (invItems ?? []) as ServiceLogItem[];
      }

      // Final fallback: no service log items, no invoice items, but
      // the property usually carries a total_beds value from signup or
      // legacy import. Seed a single Totals row from that so the
      // operator is never stuck. Bed type defaults to Large (the
      // dominant sleeper across the portfolio) — operator can split
      // into per-size or per-room rows on the Rooms tab afterwards.
      let suggestion = suggestFromServiceLog(items);
      if (suggestion.length === 0) {
        const propBeds = Math.max(0, Math.floor(Number(property.total_beds ?? 0)));
        if (propBeds > 0) {
          suggestion = [{
            room_number: 'Totals',
            room_name: 'Totals',
            items: { 'Beds: Large': propBeds },
          }];
        }
      }
      if (suggestion.length === 0) {
        setError('No service log items, invoice items, or property bed total to seed rooms from. Add rooms manually on the Rooms tab.');
        return;
      }

      const payload = suggestion.map((s) => ({
        property_id: property.id,
        room_number: s.room_number || 'Room',
        room_name: s.room_name || s.room_number || 'Room',
        room_type: 'standard',
        status: 'active',
        items: s.items,
      }));
      const { data: inserted, error: insErr } = await supabase
        .from('hosp_rooms')
        .insert(payload)
        .select('id');
      if (insErr) throw insErr;

      const newIds = ((inserted as { id: string }[] | null) ?? []).map((r) => r.id);

      // Sync totals through the SECURITY DEFINER RPC so RLS/PostgREST
      // can't swallow the property update.
      await supabase.rpc('pos_sync_property_totals', { p_property_id: property.id });

      await loadData();
      setSelectedRooms((prev) => ({ ...prev, [invoice.id]: new Set(newIds) }));
      setExpandedId(invoice.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSeedingFor(null);
    }
  }

  function openPicker(invoiceId: string) {
    // Default to all rooms pre-selected — 95% of POS services cover the
    // whole property. Operator can deselect individuals or hit None.
    setSelectedRooms(prev => ({
      ...prev,
      [invoiceId]: prev[invoiceId] && prev[invoiceId].size > 0
        ? prev[invoiceId]
        : new Set(rooms.map(r => r.id)),
    }));
    setExpandedId(invoiceId);
  }

  async function handleLink(invoice: EnrichedInvoice) {
    const roomIds = Array.from(selectedRooms[invoice.id] || []);
    if (roomIds.length === 0 && rooms.length > 0) {
      setError('Select at least one room to link');
      return;
    }
    setLinking(invoice.id);
    setError(null);
    try {
      const { data: stepRows } = await supabase.rpc('pos_calculate_service_discount', {
        p_property_id: property.id,
        p_service_date: invoice.invoice_date,
      });
      const tierStep = (stepRows as { step: number; pct: number }[] | null)?.[0]?.step ?? null;
      const tierPct = (stepRows as { step: number; pct: number }[] | null)?.[0]?.pct ?? null;

      const historyRows = roomIds.length > 0
        ? roomIds.map(roomId => ({
            property_id: property.id,
            room_id: roomId,
            invoice_id: invoice.id,
            service_date: invoice.invoice_date,
            service_type: 'standard',
            tier_discount_step: tierStep,
            tier_discount_pct: tierPct,
          }))
        : [{
            property_id: property.id,
            room_id: null,
            invoice_id: invoice.id,
            service_date: invoice.invoice_date,
            service_type: 'standard',
            tier_discount_step: tierStep,
            tier_discount_pct: tierPct,
          }];

      await supabase
        .from('hosp_service_history')
        .delete()
        .eq('property_id', property.id)
        .eq('invoice_id', invoice.id);

      const { error: histErr } = await supabase
        .from('hosp_service_history')
        .insert(historyRows);
      if (histErr) throw new Error(`Failed to save link: ${histErr.message}`);

      // Update last_service_date on selected rooms (best-effort)
      if (roomIds.length > 0 && invoice.invoice_date) {
        for (const roomId of roomIds) {
          const room = rooms.find(r => r.id === roomId);
          if (room && (!room.last_service_date || new Date(invoice.invoice_date) > new Date(room.last_service_date))) {
            await supabase.from('hosp_rooms').update({ last_service_date: invoice.invoice_date }).eq('id', roomId);
          }
        }
      }

      // Recalculate property-level stats
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      const { data: linkedInvoices } = await supabase
        .from('hosp_service_history')
        .select('invoice_id')
        .eq('property_id', property.id)
        .gte('service_date', twelveMonthsAgo.toISOString().split('T')[0]);
      const uniqueInvoiceIds = [...new Set((linkedInvoices || []).map(r => r.invoice_id))];
      const servicesCount = uniqueInvoiceIds.length;

      const { data: latestService } = await supabase
        .from('hosp_service_history')
        .select('service_date')
        .eq('property_id', property.id)
        .order('service_date', { ascending: false })
        .limit(1);
      const lastServiceDate = latestService?.[0]?.service_date || invoice.invoice_date;

      const tierDays = (TIER_FREQUENCIES as Record<string, number>)[property.hospitality_tier] || 365;
      const assuredUntil = new Date(lastServiceDate);
      assuredUntil.setDate(assuredUntil.getDate() + tierDays);

      await supabase.from('hosp_properties').update({
        last_service_date: lastServiceDate,
        services_12m: servicesCount,
        assured_until: assuredUntil.toISOString().split('T')[0],
      }).eq('id', property.id);

      if (invoice.service_log_id) {
        await supabase.from('service_logs').update({ property_id: property.id }).eq('id', invoice.service_log_id);
      }
      // Cert rows are named with the CERT- prefix, sometimes with a scope
      // suffix (e.g. "CERT-ZA-024549-All rooms"). Match by prefix, not
      // exact, so both forms get attributed to the property.
      const certRefPrefix = invoice.invoice_number.replace(/^INV-/, 'CERT-');
      await supabase.from('cert_submissions').update({
        property_id: property.id,
        service_log_id: invoice.service_log_id || undefined,
      }).ilike('invoice_number', `${certRefPrefix}%`);

      setInvoices(prev => prev.map(i => i.id === invoice.id ? { ...i, isLinked: true, linkedRoomIds: roomIds } : i));
      setExpandedId(null);
      setSuccessCount(c => c + 1);
      onLinked?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLinking(null);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-light)' }}>
        Loading invoices…
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-light)' }}>
        No invoices found for this customer.
      </div>
    );
  }

  const unlinkedCount = invoices.filter(i => !i.isLinked).length;
  const linkedCount = invoices.filter(i => i.isLinked).length;
  const canLink = mode === 'edit';

  return (
    <div>
      <div className="flex items-start justify-between mb-md gap-lg">
        <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)', flex: 1 }}>
          {unlinkedCount} unlinked, {linkedCount} linked.
          {canLink && (rooms.length > 0
            ? ' Click an invoice — all rooms are pre-selected; untick any rooms that were not serviced this visit.'
            : ' Add rooms to this property first to link at room level.')}
        </div>
        <a
          href={`/service-logs/new?property_id=${property.id}`}
          className="btn btn-sm btn-primary"
          style={{ whiteSpace: 'nowrap', textDecoration: 'none' }}
          title="Open the New Service Log form pre-filled with this property"
        >
          + New Service Log
        </a>
      </div>

      {error && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
          background: 'var(--color-error-bg, #fee2e2)', color: 'var(--color-error)',
          borderRadius: 'var(--radius-md)', fontSize: 'var(--text-base)',
        }}>
          {error}
        </div>
      )}

      {successCount > 0 && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
          background: 'var(--color-success-bg)', color: 'var(--color-success)',
          borderRadius: 'var(--radius-md)', fontSize: 'var(--text-base)',
        }}>
          {successCount} service{successCount !== 1 ? 's' : ''} linked successfully.
        </div>
      )}

      <div className="flex flex-col gap-sm">
        {invoices.map(inv => {
          const isExpanded = canLink && expandedId === inv.id;
          const selectedCount = selectedRooms[inv.id]?.size || 0;
          // Linked but 0 rooms = property-level-only. Offer a one-click
          // "Fan out to rooms" so operators can fix old records without
          // deleting and re-linking. Stays clickable even when the
          // property has no rooms yet — the expanded panel now offers a
          // Create-rooms-from-service-log seed button in that case.
          const isPropertyLevelOnly = inv.isLinked && inv.linkedRoomIds.length === 0;
          const clickable = canLink && (!inv.isLinked || isPropertyLevelOnly);
          return (
            <div
              key={inv.id}
              style={{
                border: isExpanded ? `2px solid ${POS_GOLD}` : '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                overflow: 'hidden',
                opacity: inv.isLinked && !isPropertyLevelOnly ? 0.75 : 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.625rem 0.75rem',
                  cursor: clickable ? 'pointer' : 'default',
                  background: isExpanded ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
                }}
                onClick={() => clickable && (isExpanded ? setExpandedId(null) : openPicker(inv.id))}
              >
                <div className="flex items-center flex-wrap flex-1 gap-lg">
                  <span style={{ fontSize: 'var(--text-base)', fontFamily: 'monospace', fontWeight: 600, minWidth: '110px' }}>
                    {inv.invoice_number}
                  </span>
                  <span style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
                    {formatDate(inv.invoice_date)}
                  </span>
                  <span style={{ fontSize: 'var(--text-md)', fontWeight: 500 }}>
                    {formatCurrency(Number(inv.total) || 0)}
                  </span>
                  {inv.items.length > 0 && (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)' }}>
                      {inv.items.map(i => `${i.quantity}× ${i.name}`).join(', ')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-sm">
                  {inv.isLinked && (
                    <StatusBadge
                      bg={isPropertyLevelOnly ? 'var(--color-warning-bg)' : 'var(--color-success-bg)'}
                      color={isPropertyLevelOnly ? 'var(--color-warning-text)' : 'var(--color-success)'}
                    >
                      {isPropertyLevelOnly
                        ? 'Property-level'
                        : `Linked (${inv.linkedRoomIds.length} room${inv.linkedRoomIds.length !== 1 ? 's' : ''})`}
                    </StatusBadge>
                  )}
                  {canLink && (!inv.isLinked || isPropertyLevelOnly) && (
                    <span style={{ fontSize: 'var(--text-sm)', color: POS_GOLD }}>
                      {isExpanded
                        ? 'Collapse'
                        : isPropertyLevelOnly
                          ? 'Fan out to rooms'
                          : 'Select rooms'}
                    </span>
                  )}
                  {!canLink && !inv.isLinked && (
                    <span className="text-xs text-muted">Not linked</span>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div style={{ padding: '0.75rem', borderTop: '1px solid var(--color-border)', background: 'var(--color-bg)' }}>
                  {rooms.length === 0 ? (
                    <div className="flex flex-col mb-md gap-sm">
                      <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>
                        No rooms on this property yet. Seed them from this invoice&rsquo;s service log in one click, or link at property level.
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: 'var(--text-md)' }}
                          disabled={seedingFor === inv.id}
                          onClick={() => handleSeedRoomsFromLog(inv)}
                        >
                          {seedingFor === inv.id ? 'Creating rooms…' : '✨ Create rooms from service log'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-sm">
                        <span style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>
                          Select rooms serviced ({selectedCount} of {rooms.length})
                        </span>
                        <div className="flex gap-sm">
                          <button type="button" className="btn btn-secondary"
                            style={{ padding: '0.15rem 0.5rem', fontSize: 'var(--text-xs)' }}
                            onClick={() => selectAllRooms(inv.id)}>All</button>
                          <button type="button" className="btn btn-secondary"
                            style={{ padding: '0.15rem 0.5rem', fontSize: 'var(--text-xs)' }}
                            onClick={() => selectNoneRooms(inv.id)}>None</button>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '0.375rem',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginBottom: '0.75rem',
                      }}>
                        {rooms.map(room => {
                          const isChecked = selectedRooms[inv.id]?.has(room.id) || false;
                          return (
                            <label key={room.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              padding: '0.375rem 0.5rem',
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              fontSize: 'var(--text-md)',
                              background: isChecked ? 'rgba(119, 44, 187, 0.08)' : 'var(--color-bg-secondary)',
                              border: isChecked ? `1px solid ${POS_GOLD}` : '1px solid transparent',
                            }}>
                              <input type="checkbox" checked={isChecked}
                                onChange={() => toggleRoom(inv.id, room.id)} />
                              <span className="fw-500">{room.room_number}</span>
                              {room.room_name && (
                                <span className="text-xs text-muted">
                                  {room.room_name}
                                </span>
                              )}
                              <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--color-text-light)' }}>
                                {room.bed_count || 1} bed{(room.bed_count || 1) !== 1 ? 's' : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <div className="flex justify-end gap-sm">
                    <button className="btn btn-secondary" style={{ fontSize: 'var(--text-md)' }}
                      onClick={() => setExpandedId(null)}>Cancel</button>
                    <button className="btn btn-primary" style={{ fontSize: 'var(--text-md)' }}
                      onClick={() => handleLink(inv)} disabled={linking === inv.id}>
                      {linking === inv.id
                        ? 'Linking…'
                        : rooms.length === 0
                          ? 'Link to Property'
                          : `Link ${selectedCount} Room${selectedCount !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
